import { useCallback, useMemo, useState } from 'react'
import {
  WbsItemStatus,
  WbsItemType,
} from '../../../../domain/entity/WbsItemEntity'
import { WbsItemSearchConditionV2VO } from '../../../../domain/value-object/WbsItemSearchConditionV2Vo'
import { WbsItemSearchConditionV2StorageService } from '../../../../services/storage-services/ui-states/wbsItemSearchConditionV2StorageService'

const wbsItemSearchSearchConditionV2Fields = [
  'rootUuids',
  'code',
  'types',
  'ticketTypes',
  'status',
  'priority',
  'displayName',
  'description',
  'teamUuids',
  'accountableUuids',
  'responsibleUuids',
  'assigneeUuids',
  'scheduledStartDate',
  'scheduledEndDate',
  'actualStartDate',
  'actualEndDate',
  'blank',
  'createdAt',
  'updatedAt',
  'createdByUuids',
  'updatedByUuids',
  'sprintUuid',
  'sprintUuids',
  'tagUuids',
  'codeIncludeQuantifier',
  'codeExcludeQuantifier',
  'displayNameIncludeQuantifier',
  'displayNameExcludeQuantifier',
  'descriptionIncludeQuantifier',
  'descriptionExcludeQuantifier',
  'tagIncludeQuantifier',
  'tagExcludeQuantifier',
] as const

export type WbsItemSearchSearchConditionV2 = Pick<
  WbsItemSearchConditionV2VO,
  (typeof wbsItemSearchSearchConditionV2Fields)[number]
>

const wbsItemSearchDefaultSearchCondition: WbsItemSearchSearchConditionV2 = {
  types: [WbsItemType.TASK],
  status: [
    WbsItemStatus.TODO,
    WbsItemStatus.DOING,
    WbsItemStatus.REVIEW,
    WbsItemStatus.DONE,
  ],
}

type UpdateWbsItemSearchSearchCondition = (
  updated: Partial<WbsItemSearchSearchConditionV2>
) => void
type SaveWbsItemSearchSearchCondition = () => Promise<void>
type WbsItemSearchSearchConditionUseCases = {
  updateSearchCondition: UpdateWbsItemSearchSearchCondition
  saveSearchCondition: SaveWbsItemSearchSearchCondition
  overrideSearchCondition: (
    newSearchCondition: WbsItemSearchConditionV2VO
  ) => void
  resetSearchCondition: () => void
}

export const useWbsItemSearchSearchConditionV2 = (
  functionUuid: string,
  projectUuid: string,
  defaultSearchCondition: WbsItemSearchSearchConditionV2,
  searchConditionStorageService: WbsItemSearchConditionV2StorageService
): {
  searchCondition: WbsItemSearchConditionV2VO
} & WbsItemSearchSearchConditionUseCases => {
  const [searchCondition, setSearchCondition] =
    useState<WbsItemSearchSearchConditionV2>(defaultSearchCondition)

  const updateSearchCondition = useCallback(
    (updated: Partial<WbsItemSearchSearchConditionV2>) => {
      setSearchCondition(prev => ({
        ...prev,
        ...updated,
      }))
    },
    []
  )

  const overrideSearchCondition = useCallback(
    (newSearchCondition: WbsItemSearchConditionV2VO) => {
      setSearchCondition(newSearchCondition)
    },
    []
  )

  const saveSearchCondition = useCallback(async () => {
    searchConditionStorageService.save(
      functionUuid,
      projectUuid,
      searchCondition
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCondition])

  const resetSearchCondition = useCallback(() => {
    setSearchCondition(wbsItemSearchDefaultSearchCondition)
  }, [])

  const returnVal = useMemo(
    () => ({
      searchCondition,
      updateSearchCondition,
      saveSearchCondition,
      overrideSearchCondition,
      resetSearchCondition,
    }),
    [
      searchCondition,
      updateSearchCondition,
      saveSearchCondition,
      overrideSearchCondition,
      resetSearchCondition,
    ]
  )

  return returnVal
}
